import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SideNav from '../SideNav/SideNav';
import BottomNav from '../BottomNav/BottomNav';

const SIDE_NAV_COLLAPSED_KEY = 'snipe.sideNav.collapsed';

function DashboardLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, hasRole } = useAuth();
    const isAdmin = hasRole('ADMIN');
    const canUseSnipeHub = true;
    const canUsePropTool = true;
    // Phased rollout — remove this gate once public community tips go GA.
    const canUseCommunityTips = hasRole('PUBLIC_TIPS');

    // Read the persisted preference synchronously in the initializer so the
    // sidebar renders in the correct mode on first paint (no FOUC / layout
    // shift). Wrapped in try/catch for SSR / privacy-mode safety. New users
    // (no stored preference) get the collapsed view by default to maximise
    // horizontal space; existing user choices are respected verbatim.
    const [sideNavCollapsed, setSideNavCollapsed] = useState(() => {
        try {
            const stored = window.localStorage.getItem(SIDE_NAV_COLLAPSED_KEY);
            if (stored === null) return true;
            return stored === '1';
        } catch (_e) {
            return true;
        }
    });

    useEffect(() => {
        try {
            window.localStorage.setItem(SIDE_NAV_COLLAPSED_KEY, sideNavCollapsed ? '1' : '0');
        } catch (_e) { /* no-op */ }
    }, [sideNavCollapsed]);

    const routeToTab = {
        '/discord': 'discord',
        '/manage-subscriptions': 'manage-subscriptions',
        '/snipe-hub': 'snipe-hub',
        '/snipe-feed': 'snipe-feed',
        '/odds-screen': 'odds-screen',
        '/hot-props': 'hot-props',
        '/multi-builder': 'multi-builder',
        '/prop-tool': 'prop-tool',
        '/results-tracker': 'results-tracker',
        '/community-tips': 'community-tips',
        '/admin-tools': 'admin-tools',
    };

    const defaultTab = user?.discordLinked ? (canUseSnipeHub ? 'snipe-hub' : 'snipe-feed') : 'discord';
    // Nested hub pages (e.g. /snipe-hub/match/:id) keep the Snipe Hub tab lit.
    const activeTab = routeToTab[location.pathname]
        || (location.pathname.startsWith('/snipe-hub/') ? 'snipe-hub' : defaultTab);

    const allMenuItems = [
        { id: 'discord', label: 'Discord', route: '/discord', showWhen: true },
        { id: 'manage-subscriptions', label: 'Manage Subscriptions', route: '/manage-subscriptions', showWhen: true },
        { id: 'snipe-hub', label: 'Snipe Hub', route: '/snipe-hub', showWhen: canUseSnipeHub },
        { id: 'prop-tool', label: 'Prop Tool', route: '/prop-tool', showWhen: canUsePropTool },
        { id: 'snipe-feed', label: 'Snipe Feed', route: '/snipe-feed', showWhen: true },
        { id: 'odds-screen', label: 'Odds Screen', route: '/odds-screen', showWhen: true },
        { id: 'hot-props', label: 'Hot Props', route: '/hot-props', showWhen: true },
        { id: 'multi-builder', label: 'Multi Builder', route: '/multi-builder', showWhen: true },
        { id: 'results-tracker', label: 'Results Tracker', route: '/results-tracker', showWhen: true },
        { id: 'community-tips', label: 'Community Tips', route: '/community-tips', showWhen: canUseCommunityTips },
        { id: 'admin-tools', label: 'Admin Tools', route: '/admin-tools', showWhen: isAdmin },
    ];

    const menuItems = allMenuItems.filter(item => item.showWhen);

    const handleTabChange = (tabId) => {
        const menuItem = menuItems.find(item => item.id === tabId);
        if (menuItem) {
            navigate(menuItem.route);
        }
    };

    const handleBottomNavChange = (tabId) => {
        const routes = {
            'discord': '/discord',
            'manage-subscriptions': '/manage-subscriptions',
            'snipe-hub': '/snipe-hub',
            'snipe-feed': '/snipe-feed',
            'odds-screen': '/odds-screen',
            'hot-props': '/hot-props',
            'multi-builder': '/multi-builder',
            'prop-tool': '/prop-tool',
            'results-tracker': '/results-tracker',
            'community-tips': '/community-tips',
            'admin-tools': '/admin-tools',
        };
        if (routes[tabId]) {
            navigate(routes[tabId]);
        }
    };

    return (
        <div className="min-h-screen bg-[#020817]">
            {/* Sidebar - desktop only */}
            <SideNav
                activeTab={activeTab}
                setActiveTab={handleTabChange}
                collapsed={sideNavCollapsed}
                onToggleCollapsed={() => setSideNavCollapsed((v) => !v)}
            />

            {/* Main Content Wrapper. The desktop margin tracks the sidebar
                width; both classes are written as full literals so Tailwind's
                JIT picks them up. */}
            <div
                className={`min-h-screen flex flex-col ml-0 transition-[margin] duration-200 ${
                    sideNavCollapsed ? 'md:ml-[72px]' : 'md:ml-[104px]'
                }`}
            >
                {/* Page Content */}
                <main className="flex-1 flex flex-col pt-0 pb-16 md:pb-0">
                    <Outlet />
                </main>
            </div>

            {/* Bottom Navigation - mobile only */}
            <BottomNav
                activeTab={activeTab}
                onTabChange={handleBottomNavChange}
            />
        </div>
    );
}

export default DashboardLayout;
